import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  Image,
  AttachFile
} from '@mui/icons-material';

const FileUpload = ({
  onFileSelect,
  onFileRemove,
  acceptedTypes = ['image/*'],
  maxSize = 5 * 1024 * 1024, // 5MB
  multiple = false,
  value = null,
  label = 'Upload File',
  helperText = 'Click to upload or drag and drop',
  error = false,
  disabled = false,
  sx = {}
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Check file size
    if (file.size > maxSize) {
      setErrorMessage(`File size must be less than ${Math.round(maxSize / (1024 * 1024))}MB`);
      return false;
    }

    // Check file type
    const isValidType = acceptedTypes.some(type => {
      if (type.endsWith('/*')) {
        return file.type.startsWith(type.replace('/*', '/'));
      }
      return file.type === type;
    });

    if (!isValidType) {
      setErrorMessage(`File type not supported. Accepted types: ${acceptedTypes.join(', ')}`);
      return false;
    }

    return true;
  };

  const handleFiles = (files) => {
    setErrorMessage('');
    const fileList = Array.from(files);
    
    if (!multiple && fileList.length > 1) {
      setErrorMessage('Only one file is allowed');
      return;
    }

    const validFiles = fileList.filter(validateFile);
    
    if (validFiles.length > 0) {
      if (multiple) {
        onFileSelect(validFiles);
      } else {
        onFileSelect(validFiles[0]);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleFileInput = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  };

  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleRemove = () => {
    onFileRemove();
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return <Image />;
    }
    return <AttachFile />;
  };

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes.join(',')}
        multiple={multiple}
        onChange={handleFileInput}
        style={{ display: 'none' }}
        disabled={disabled}
      />

      {!value ? (
        <Paper
          elevation={dragActive ? 8 : 2}
          sx={{
            p: 3,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: dragActive ? '2px dashed #4CAF50' : '2px dashed #ccc',
            backgroundColor: dragActive ? '#E8F5E8' : 'transparent',
            transition: 'all 0.3s ease',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              backgroundColor: disabled ? 'transparent' : '#E8F5E8',
              borderColor: disabled ? '#ccc' : '#4CAF50'
            }
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <CloudUpload sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {helperText}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Max size: {Math.round(maxSize / (1024 * 1024))}MB
          </Typography>
        </Paper>
      ) : (
        <Box>
          {Array.isArray(value) ? (
            <Box>
              {value.map((file, index) => (
                <Chip
                  key={index}
                  icon={getFileIcon(file)}
                  label={`${file.name} (${formatFileSize(file.size)})`}
                  onDelete={handleRemove}
                  sx={{ 
                    mr: 1, 
                    mb: 1,
                    backgroundColor: '#E8F5E8',
                    borderColor: '#4CAF50',
                    color: '#2E7D32',
                    '& .MuiChip-deleteIcon': {
                      color: '#2E7D32',
                      '&:hover': {
                        color: '#1B5E20'
                      }
                    }
                  }}
                  variant="outlined"
                />
              ))}
            </Box>
          ) : (
            <Chip
              icon={getFileIcon(value)}
              label={`${value.name} (${formatFileSize(value.size)})`}
              onDelete={handleRemove}
              sx={{ 
                backgroundColor: '#E8F5E8',
                borderColor: '#4CAF50',
                color: '#2E7D32',
                '& .MuiChip-deleteIcon': {
                  color: '#2E7D32',
                  '&:hover': {
                    color: '#1B5E20'
                  }
                }
              }}
              variant="outlined"
            />
          )}
        </Box>
      )}

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="caption" color="text.secondary">
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {errorMessage && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Please select a file
        </Alert>
      )}
    </Box>
  );
};

export default FileUpload;
